import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Clock, X, Check, Trash2, User, Repeat } from 'lucide-react';
import { format, addMonths } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATIONS = [
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' },
];

const RECURRENCE_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' },
];

export const ScheduleModal = ({ job, isOpen, onClose, onScheduled }) => {
  const { user } = useAuth();
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [duration, setDuration] = useState(60);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  
  // Team assignment (Crew only)
  const [teamMembers, setTeamMembers] = useState([]);
  const [assignedTo, setAssignedTo] = useState(null);
  
  // Recurring (Crew only)
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState('weekly');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(null);
  const [planFeatures, setPlanFeatures] = useState({});
  
  // Modification scope for recurring jobs
  const [modifyScope, setModifyScope] = useState('this_only');

  const isCrew = user?.subscription_tier === 'crew';
  const hasRecurring = planFeatures?.has_recurring ?? isCrew;
  
  // Check if this job is part of a recurring series
  const isPartOfRecurring = job?.parent_job_id || job?.is_recurring;

  // Reset state when modal opens or job changes
  useEffect(() => {
    if (isOpen && job) {
      setDate(job.scheduled_date ? new Date(job.scheduled_date) : null);
      setTime(job.scheduled_time || null);
      setDuration(job.duration || 60);
      setAssignedTo(job.assigned_to || user?.id);
      setIsRecurring(job.is_recurring || false);
      setRecurrencePattern(job.recurrence_pattern || 'weekly');
      setRecurrenceEndDate(job.recurrence_end_date ? new Date(job.recurrence_end_date) : addMonths(new Date(), 3));
      setModifyScope('this_only'); // Default to modifying only this occurrence
    }
  }, [isOpen, job, user?.id]);

  useEffect(() => {
    if (date) {
      fetchAvailableSlots(format(date, 'yyyy-MM-dd'));
    }
  }, [date]);

  useEffect(() => {
    if (isCrew && isOpen) {
      fetchTeamMembers();
    }
  }, [isCrew, isOpen]);

  // Fetch plan features to check for recurring
  useEffect(() => {
    const fetchPlanFeatures = async () => {
      try {
        const response = await axios.get(`${API}/jobs/limits`);
        setPlanFeatures(response.data.plan_limits || {});
      } catch (error) {
        console.error('Failed to fetch plan features');
      }
    };
    if (isOpen) {
      fetchPlanFeatures();
    }
  }, [isOpen]);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team/members`);
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch team members');
    }
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    setCalendarOpen(false);
  };

  const handleEndDateSelect = (selectedDate) => {
    setRecurrenceEndDate(selectedDate);
    setEndDateOpen(false);
  };

  const fetchAvailableSlots = async (dateStr) => {
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/jobs/available-slots/${dateStr}`);
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      toast.error('Failed to load available times');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSave = async () => {
    if (!date || !time) {
      toast.error('Please select a date and time');
      return;
    }

    // Validate recurring end date if recurring is enabled (and not modifying this_only)
    if (isRecurring && modifyScope !== 'this_only' && !recurrenceEndDate) {
      toast.error('Please select an end date for recurring appointments');
      return;
    }

    setSaving(true);
    try {
      const scheduleData = {
        scheduled_date: date.toISOString(),
        scheduled_time: time,
        duration: duration
      };

      // Add assignment if Crew
      if (isCrew && assignedTo) {
        scheduleData.assigned_to = assignedTo;
      }

      // For recurring jobs, add the modification scope
      if (isPartOfRecurring) {
        scheduleData.modify_scope = modifyScope;
      }

      // Add recurring if enabled (and not modifying a single occurrence)
      if (isRecurring && hasRecurring && modifyScope !== 'this_only') {
        scheduleData.is_recurring = true;
        scheduleData.recurrence_pattern = recurrencePattern;
        scheduleData.recurrence_end_date = recurrenceEndDate?.toISOString();
      }

      const response = await axios.post(`${API}/jobs/${job.id}/schedule`, scheduleData);
      
      const successMessage = isPartOfRecurring && modifyScope === 'this_only'
        ? 'This appointment updated!'
        : isRecurring 
          ? 'Recurring schedule updated!' 
          : 'Job scheduled!';
      
      toast.success(successMessage);
      onScheduled(response.data);
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to schedule job';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSchedule = async () => {
    setRemoving(true);
    try {
      const response = await axios.post(`${API}/jobs/${job.id}/remove-schedule`);
      toast.success('Appointment removed');
      onScheduled(response.data);
      onClose();
    } catch (error) {
      console.error('Remove schedule error:', error);
      const message = error.response?.data?.detail || 'Failed to remove schedule';
      toast.error(message);
    } finally {
      setRemoving(false);
    }
  };

  const formatTimeDisplay = (timeStr) => {
    const [hour, min] = timeStr.split(':').map(Number);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
  };

  if (!isOpen) return null;

  const hasExistingSchedule = job?.scheduled_date && job?.scheduled_time;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-[60] overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="min-h-full flex items-start sm:items-center justify-center p-4 pb-24 sm:pb-4">
        <div className="bg-white rounded-2xl w-full max-w-md my-4 sm:my-8 animate-slide-up flex flex-col max-h-[calc(100vh-6rem)] sm:max-h-[calc(100vh-4rem)]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-shrink-0">
            <h2 className="font-heading text-xl font-bold text-slate-900">Schedule Job</h2>
            <button
              onClick={onClose}
              data-testid="close-schedule-modal"
              className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="p-4 overflow-y-auto flex-1 overscroll-contain">
            {/* Modification Scope for Recurring Jobs */}
            {isPartOfRecurring && hasExistingSchedule && (
              <div className="mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <Label className="text-amber-700 uppercase text-xs tracking-wider font-bold mb-3 block flex items-center gap-2">
                  <Repeat className="w-4 h-4" />
                  Modify Recurring Appointment
                </Label>
                <div className="space-y-2">
                  <button
                    onClick={() => setModifyScope('this_only')}
                    data-testid="modify-this-only"
                    className={`w-full p-3 rounded-lg text-sm font-medium transition-colors text-left flex items-center gap-3 ${
                      modifyScope === 'this_only'
                        ? 'bg-amber-500 text-white'
                        : 'bg-white text-amber-700 hover:bg-amber-100 border border-amber-200'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      modifyScope === 'this_only' ? 'border-white' : 'border-amber-400'
                    }`}>
                      {modifyScope === 'this_only' && <div className="w-2 h-2 bg-white rounded-full" />}
                    </div>
                    <div>
                      <div className="font-bold">This occurrence only</div>
                      <div className={`text-xs ${modifyScope === 'this_only' ? 'text-amber-100' : 'text-amber-600'}`}>
                        Change just this appointment
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setModifyScope('all_future')}
                    data-testid="modify-all-future"
                    className={`w-full p-3 rounded-lg text-sm font-medium transition-colors text-left flex items-center gap-3 ${
                      modifyScope === 'all_future'
                        ? 'bg-amber-500 text-white'
                        : 'bg-white text-amber-700 hover:bg-amber-100 border border-amber-200'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      modifyScope === 'all_future' ? 'border-white' : 'border-amber-400'
                    }`}>
                      {modifyScope === 'all_future' && <div className="w-2 h-2 bg-white rounded-full" />}
                    </div>
                    <div>
                      <div className="font-bold">This and all future</div>
                      <div className={`text-xs ${modifyScope === 'all_future' ? 'text-amber-100' : 'text-amber-600'}`}>
                        Update the recurring pattern
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Team Assignment (Crew only) */}
            {isCrew && teamMembers.length > 0 && (
              <div className="mb-4">
                <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold mb-2 block">
                  <User className="w-4 h-4 inline mr-1" />
                  Assign To
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {teamMembers.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => setAssignedTo(member.id)}
                      className={`p-3 rounded-lg text-sm font-medium transition-colors text-left ${
                        assignedTo === member.id
                          ? 'bg-[#FF5F00] text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {member.name}
                      {member.id === user?.id && ' (Me)'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Date Picker */}
            <div className="mb-4">
              <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold mb-2 block">
                Select Date
              </Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button
                    data-testid="schedule-date-picker"
                    className="w-full h-12 px-4 bg-white border-2 border-slate-300 rounded-lg flex items-center gap-3 text-left hover:border-[#FF5F00]"
                  >
                    <CalendarIcon className="w-5 h-5 text-slate-400" />
                    <span className={date ? 'text-slate-900' : 'text-slate-400'}>
                      {date ? format(date, 'EEEE, MMM d, yyyy') : 'Pick a date'}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={handleDateSelect}
                    disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Duration */}
            <div className="mb-4">
              <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold mb-2 block">
                Duration
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {DURATIONS.map((d) => (
                  <button
                    key={d.value}
                    onClick={() => setDuration(d.value)}
                    className={`h-10 rounded-lg font-medium text-sm transition-colors ${
                      duration === d.value
                        ? 'bg-[#FF5F00] text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Slots */}
            {date && (
              <div className="mb-4">
                <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold mb-2 block">
                  Available Times
                </Label>
                
                {loadingSlots ? (
                  <div className="text-center py-4 text-slate-500">Loading available times...</div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot.time}
                        onClick={() => slot.available && setTime(slot.time)}
                        disabled={!slot.available}
                        data-testid={`time-slot-${slot.time}`}
                        className={`h-10 rounded-lg text-sm font-medium transition-colors ${
                          time === slot.time
                            ? 'bg-[#FF5F00] text-white'
                            : slot.available
                              ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                              : 'bg-slate-50 text-slate-300 cursor-not-allowed line-through'
                        }`}
                      >
                        {formatTimeDisplay(slot.time)}
                      </button>
                    ))}
                  </div>
                )}
                
                {availableSlots.length > 0 && (
                  <p className="text-slate-400 text-xs mt-2">
                    Grayed out times are already booked
                  </p>
                )}
              </div>
            )}

            {/* Recurring Options (Premium feature) */}
            {hasRecurring && (
              <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-purple-700 uppercase text-xs tracking-wider font-bold flex items-center gap-2">
                    <Repeat className="w-4 h-4" />
                    Recurring Appointment
                  </Label>
                  <Switch
                    checked={isRecurring}
                    onCheckedChange={setIsRecurring}
                    data-testid="recurring-toggle"
                  />
                </div>
                
                {isRecurring && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      {RECURRENCE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setRecurrencePattern(opt.value)}
                          className={`p-2 rounded-lg text-xs font-medium transition-colors ${
                            recurrencePattern === opt.value
                              ? 'bg-purple-600 text-white'
                              : 'bg-white text-purple-700 hover:bg-purple-100'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    
                    <div>
                      <Label className="text-purple-600 text-xs font-medium mb-1 block">
                        Repeat Until
                      </Label>
                      <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                        <PopoverTrigger asChild>
                          <button className="w-full h-10 px-3 bg-white border border-purple-200 rounded-lg flex items-center gap-2 text-sm text-left">
                            <CalendarIcon className="w-4 h-4 text-purple-400" />
                            {recurrenceEndDate ? format(recurrenceEndDate, 'MMM d, yyyy') : 'Select end date'}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={recurrenceEndDate}
                            onSelect={handleEndDateSelect}
                            disabled={(d) => d < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Selected Summary */}
            {date && time && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 text-emerald-700">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">
                    {format(date, 'EEE, MMM d')} at {formatTimeDisplay(time)} ({DURATIONS.find(d => d.value === duration)?.label})
                  </span>
                </div>
                {isCrew && assignedTo && (
                  <p className="text-emerald-600 text-sm mt-1">
                    Assigned to: {teamMembers.find(m => m.id === assignedTo)?.name || 'Team member'}
                  </p>
                )}
                {isRecurring && (
                  <p className="text-purple-600 text-sm mt-1">
                    🔄 Repeats {recurrencePattern} until {recurrenceEndDate ? format(recurrenceEndDate, 'MMM d, yyyy') : 'TBD'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer - Fixed at Bottom */}
          <div className="p-4 border-t border-slate-200 flex-shrink-0 space-y-2 bg-white">
            <Button
              onClick={handleSave}
              disabled={!date || !time || saving}
              data-testid="save-schedule"
              className="w-full h-12 bg-[#FF5F00] hover:bg-orange-600 text-white font-bold rounded-lg"
            >
              {saving ? 'Saving...' : 
                isPartOfRecurring && modifyScope === 'this_only' ? 'Update This Appointment' :
                isRecurring ? 'Create Recurring Schedule' : 
                hasExistingSchedule ? 'Update Schedule' : 'Schedule Job'}
            </Button>
            
            {hasExistingSchedule && (
              <Button
                onClick={handleRemoveSchedule}
                disabled={removing}
                data-testid="remove-schedule"
                variant="outline"
                className="w-full h-10 text-red-600 border-red-200 hover:bg-red-50 flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {removing ? 'Removing...' : 'Remove Appointment'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
